<template>
    <div class="single-suggest-input position-relative">
        <input :id="inputId"
               ref="input"
               type="text"
               class="form-control"
               :class="size ? 'form-control-' + size : ''"
               autocomplete="off"
               :value="value"
               :disabled="disabled"
               :placeholder="placeholder"
               @input="onInput"
               @focus="onFocus"
               @blur="onBlur"
               @keydown="onKeydown">
        <div v-if="dropdownVisible" class="ssi-dropdown">
            <div v-if="loading" class="ssi-item text-muted">
                <i class="fa fa-fw fa-spinner fa-spin"></i>
            </div>
            <template v-else-if="suggestions.length">
                <div v-for="(item, index) in suggestions"
                     :key="item.value + '#' + index"
                     :class="['ssi-item', {'ssi-item-active': index === activeIndex}]"
                     :title="item.label"
                     @mousedown.prevent="choose(item)"
                     @mouseenter="activeIndex = index">
                    <slot name="item" :item="item">{{ item.label }}</slot>
                </div>
            </template>
            <div v-else class="ssi-item ssi-empty text-muted small">{{ emptyText }}</div>
        </div>
    </div>
</template>

<script>
/**
 * 单值远程联想输入框（逻辑同 MultiSearchSelect：300 ms 防抖 / 丢弃过期响应 / 键盘上下回车，改成单值 + 外部传 fetch）。
 *
 * 用法：
 *   <single-suggest-input v-model="orderCode" :fetch="fetchOrders" @select="onOrderSelect"/>
 *   fetchOrders(keyword) => Promise<Array<{value, label, raw}>>
 *
 * 行为：
 * 1. v-model 随输入变化（允许自由输入，不必选中候选）；
 * 2. 关键字 trim 后长度 ≥ minLength 时防抖请求 fetch，候选以下拉展示；minLength=0 时聚焦即拉一次（适合「列出全部再过滤」）；
 * 3. 点选或上下键 + 回车选中：v-model 置为 item.value，并 emit select(item)；
 * 4. 回车总是 preventDefault（放在 form 里不会误提交外层表单），没有高亮候选时 emit enter；Esc 收起下拉；
 * 5. 候选内容可用 #item="{item}" 插槽自定义（插槽里的按钮要 @mousedown.stop.prevent，避免触发选中）。
 */
const DEBOUNCE_MS = 300;

export default {
    name: "SingleSuggestInput",
    props: {
        value: {
            type: String,
            default: ""
        },
        // (keyword: string) => Promise<Array<{value: string, label: string, raw: any}>>
        fetch: {
            type: Function,
            required: true
        },
        minLength: {
            type: Number,
            default: 2
        },
        placeholder: {
            type: String,
            default: ""
        },
        disabled: {
            type: Boolean,
            default: false
        },
        // 无匹配时下拉里的提示；空串则无匹配时不弹下拉
        emptyText: {
            type: String,
            default: ""
        },
        inputId: {
            type: String,
            default: undefined
        },
        // Bootstrap 尺寸：'sm' | 'lg' | ''
        size: {
            type: String,
            default: "sm"
        }
    },
    data() {
        return {
            suggestions: [],
            loading: false,
            // 输入框真实焦点
            focused: false,
            // 用户选中 / Esc 后收起下拉；再输入、再聚焦或按下键时重新展开
            closed: false,
            // 已为当前关键字拿到结果（区分「还没查」与「查了没有」）
            fetched: false,
            activeIndex: -1
        };
    },
    computed: {
        keyword() {
            return (this.value || "").trim();
        },
        dropdownVisible() {
            if (!this.focused || this.closed || this.keyword.length < this.minLength) {
                return false;
            }
            return this.loading || this.suggestions.length > 0 || (this.fetched && !!this.emptyText);
        }
    },
    created() {
        // 非响应式：防抖定时器、请求代次（只认最后一次请求的响应）、最近一次选中的值
        this.timer = null;
        this.seq = 0;
        this.chosenValue = null;
    },
    beforeDestroy() {
        this.clearTimer();
    },
    methods: {
        clearTimer() {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
        },
        onInput(event) {
            this.$emit("input", event.target.value);
            this.closed = false;
            this.activeIndex = -1;
            this.fetched = false;
            this.chosenValue = null;
            this.schedule(event.target.value);
        },
        schedule(text) {
            this.clearTimer();
            const keyword = (text || "").trim();
            if (keyword.length < this.minLength) {
                this.seq++;
                this.suggestions = [];
                this.loading = false;
                return;
            }
            this.timer = setTimeout(() => this.load(keyword), DEBOUNCE_MS);
        },
        onFocus() {
            this.focused = true;
            this.closed = false;
            if (this.keyword.length < this.minLength || this.loading) {
                return;
            }
            // minLength=0 且框里是刚选中的值：重新聚焦时列出全部，方便换一条
            if (this.minLength === 0 && this.chosenValue !== null && this.value === this.chosenValue) {
                this.clearTimer();
                this.load("");
            } else if (!this.fetched) {
                this.clearTimer();
                this.load(this.keyword);
            }
        },
        onBlur() {
            this.focused = false;
            this.activeIndex = -1;
        },
        /** 重新拉取当前关键字的候选（外部删除了某条候选后调用） */
        refresh() {
            this.clearTimer();
            if (this.keyword.length >= this.minLength) {
                return this.load(this.chosenValue !== null && this.value === this.chosenValue ? "" : this.keyword);
            }
            this.suggestions = [];
            return Promise.resolve();
        },
        /** 把焦点放回输入框 */
        focus() {
            if (this.$refs.input) {
                this.$refs.input.focus();
            }
        },
        load(keyword) {
            const seq = ++this.seq;
            this.loading = true;
            let promise;
            try {
                promise = Promise.resolve(this.fetch(keyword));
            } catch (e) {
                promise = Promise.reject(e);
            }
            return promise.then(list => {
                // 请求返回时用户可能已经改了输入，丢弃过期结果
                if (seq !== this.seq) {
                    return;
                }
                this.suggestions = Array.isArray(list) ? list : [];
                this.activeIndex = -1;
                this.fetched = true;
            }).catch(() => {
                if (seq === this.seq) {
                    this.suggestions = [];
                    this.fetched = true;
                }
            }).finally(() => {
                if (seq === this.seq) {
                    this.loading = false;
                }
            });
        },
        choose(item) {
            this.clearTimer();
            this.seq++;
            this.loading = false;
            this.activeIndex = -1;
            this.closed = true;
            // 候选是按旧关键字查的：下次聚焦按新值重查
            this.fetched = false;
            this.chosenValue = item.value;
            this.$emit("input", item.value);
            this.$emit("select", item);
        },
        onKeydown(event) {
            const count = this.dropdownVisible ? this.suggestions.length : 0;
            if (event.key === "Enter") {
                event.preventDefault();
                if (count && this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
                    this.choose(this.suggestions[this.activeIndex]);
                } else {
                    this.$emit("enter", this.value);
                }
            } else if (event.key === "ArrowDown") {
                event.preventDefault();
                if (this.closed || !this.fetched) {
                    // 收起后按下键：重新展开（必要时重查）
                    this.onFocus();
                    return;
                }
                if (count) {
                    this.activeIndex = (this.activeIndex + 1) % count;
                }
            } else if (event.key === "ArrowUp" && count) {
                event.preventDefault();
                this.activeIndex = (this.activeIndex - 1 + count) % count;
            } else if (event.key === "Escape" && this.dropdownVisible) {
                // 在弹窗里时只收起下拉、不关弹窗
                event.preventDefault();
                event.stopPropagation();
                this.closed = true;
                this.activeIndex = -1;
            }
        }
    }
};
</script>

<style scoped>
.ssi-dropdown {
    position: absolute;
    z-index: 1060;
    left: 0;
    top: 100%;
    min-width: 100%;
    max-width: 480px;
    max-height: 260px;
    overflow-y: auto;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, .15);
    border-radius: .25rem;
    box-shadow: 0 .25rem .5rem rgba(0, 0, 0, .15);
}

.ssi-item {
    padding: .25rem .5rem;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ssi-empty {
    cursor: default;
}

.ssi-item-active {
    background-color: #f0f4ff;
}
</style>
